import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { fmt } from "./convoTraceFormat";

// Horizontal gauge: fill = score, tick = threshold. Scores are cosine
// similarities in [0, 1], so the 0–1 track is the honest range.
const Gauge = (props) => {
  const { value, threshold, color } = props;
  const pct = (n) => `${Math.min(100, Math.max(0, n * 100))}%`;
  return (
    <Box sx={{ position: "relative", height: 10, borderRadius: 1, bgcolor: "action.selected", my: 0.75 }}>
      <Box sx={{ position: "absolute", inset: 0, width: pct(value), bgcolor: color, borderRadius: 1 }} />
      {threshold != null && (
        <Box
          title={`threshold ${threshold}`}
          sx={{
            position: "absolute",
            top: -5,
            bottom: -5,
            left: pct(threshold),
            transform: "translateX(-50%)",
            width: "4px",
            borderRadius: 1,
            bgcolor: "text.primary",
            boxShadow: "0 0 0 1.5px rgba(255,255,255,0.85)",
          }}
        />
      )}
    </Box>
  );
};

Gauge.propTypes = {
  value: PropTypes.number.isRequired,
  threshold: PropTypes.number,
  color: PropTypes.string.isRequired,
};

// Turns the confidence decision into a one-glance visual: how the winning score
// sits against the bar it had to clear (skip-terms, or this question's outlier
// threshold), or why there was no bar to clear (too few candidates).
const ConfidenceMeter = (props) => {
  const { trace } = props;
  const stages = trace.stages || {};
  const skip = stages.skip_terms;
  const outlier = stages.outlier;
  const caption = (text) => (
    <Typography variant="caption" sx={{ color: "text.secondary" }}>
      {text}
    </Typography>
  );

  if ((trace.decision || "").startsWith("exact")) {
    return caption("Exact text match — maximum confidence.");
  }
  if (skip?.triggered) {
    return (
      <Box>
        {caption("Semantic confidence")}
        <Gauge value={skip.top_semantic_score} threshold={skip.threshold} color="success.main" />
        {caption(`top ${fmt(skip.top_semantic_score)} cleared the ${skip.threshold} skip-terms bar`)}
      </Box>
    );
  }
  if (outlier?.threshold != null) {
    const gap = outlier.top_score - outlier.threshold;
    return (
      <Box>
        {caption("Confidence vs this question's bar")}
        <Gauge
          value={outlier.top_score}
          threshold={outlier.threshold}
          color={outlier.is_outlier ? "success.main" : "warning.main"}
        />
        {caption(
          `top ${fmt(outlier.top_score)} ${outlier.is_outlier ? "cleared" : "fell short of"} the per-question threshold ${fmt(outlier.threshold)} (${gap >= 0 ? "+" : ""}${gap.toFixed(4)}) across ${outlier.num_candidates} candidates`,
        )}
      </Box>
    );
  }
  if (outlier?.num_candidates != null) {
    return caption(
      `Only ${outlier.num_candidates} candidate bucket(s) — the matcher won't pick from fewer than 4, so it asks for clarification.`,
    );
  }
  return null;
};

ConfidenceMeter.propTypes = {
  trace: PropTypes.shape({
    decision: PropTypes.string,
    stages: PropTypes.object,
  }).isRequired,
};

export default ConfidenceMeter;
